import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { ChevronDown, ChevronRight, Shield } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/enterprise`;

const PermissionMatrix = ({ selectedPermissions = [], onChange }) => {
  const [categories, setCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPermissionCategories();
  }, []);

  const fetchPermissionCategories = async () => {
    try {
      const response = await axios.get(`${API}/permissions/categories`);
      setCategories(response.data);
      // Expand all categories by default
      setExpandedCategories(new Set(response.data.map(c => c.category)));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching permission categories:", error);
      setLoading(false);
    }
  };

  const toggleCategory = (category) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const handlePermissionToggle = (permissionId) => {
    const newPermissions = selectedPermissions.includes(permissionId)
      ? selectedPermissions.filter(id => id !== permissionId)
      : [...selectedPermissions, permissionId];
    
    if (onChange) {
      onChange(newPermissions);
    }
  };

  const handleSelectAll = (categoryPermissions) => {
    const categoryPermissionIds = categoryPermissions.map(p => p.id);
    const allSelected = categoryPermissionIds.every(id => selectedPermissions.includes(id));
    
    let newPermissions;
    if (allSelected) {
      // Deselect all in this category
      newPermissions = selectedPermissions.filter(id => !categoryPermissionIds.includes(id));
    } else {
      // Select all in this category
      const toAdd = categoryPermissionIds.filter(id => !selectedPermissions.includes(id));
      newPermissions = [...selectedPermissions, ...toAdd];
    }
    
    if (onChange) {
      onChange(newPermissions);
    }
  };

  const applyTemplate = (template) => {
    let templatePermissions = [];
    
    switch (template) {
      case "manager":
        // Manager: Most permissions except destructive ones
        templatePermissions = categories.flatMap(cat => 
          cat.permissions
            .filter(p => !p.id.includes("delete") && !p.id.includes("manage_admins"))
            .map(p => p.id)
        );
        break;
      case "analyst":
        // Analyst: View and analyze permissions
        templatePermissions = categories.flatMap(cat =>
          cat.permissions
            .filter(p => p.id.includes("view") || p.id.includes("generate") || p.id.includes("export") || p.id.includes("analysis"))
            .map(p => p.id)
        );
        break;
      case "viewer":
        // Viewer: Only view permissions
        templatePermissions = categories.flatMap(cat =>
          cat.permissions
            .filter(p => p.id.includes("view"))
            .map(p => p.id)
        );
        break;
      default:
        templatePermissions = [];
    }
    
    if (onChange) {
      onChange(templatePermissions);
    }
  };

  const totalPermissions = categories.reduce((sum, cat) => sum + cat.permissions.length, 0);
  const selectedCount = selectedPermissions.length;

  if (loading) {
    return <div className="text-center py-4">Loading permissions...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header with count and templates */}
      <div className="flex items-center justify-between pb-3 border-b">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-slate-600" />
          <span className="font-medium">
            {selectedCount} of {totalPermissions} permissions selected
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">Quick Templates:</span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => applyTemplate("manager")}
          >
            Manager
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => applyTemplate("analyst")}
          >
            Analyst
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => applyTemplate("viewer")}
          >
            Viewer
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onChange([])}
          >
            Reset
          </Button>
        </div>
      </div>

      {/* Permission categories */}
      <div className="space-y-3">
        {categories.map((category) => {
          const isExpanded = expandedCategories.has(category.category);
          const categoryPermissionIds = category.permissions.map(p => p.id);
          const categorySelectedCount = categoryPermissionIds.filter(id => 
            selectedPermissions.includes(id)
          ).length;
          const allSelected = categorySelectedCount === category.permissions.length;

          return (
            <div key={category.category} className="border rounded-lg overflow-hidden">
              {/* Category header */}
              <div
                className="flex items-center justify-between p-3 bg-slate-50 cursor-pointer hover:bg-slate-100"
                onClick={() => toggleCategory(category.category)}
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                  <span className="font-medium">{category.category}</span>
                  <span className="text-sm text-slate-600">
                    ({categorySelectedCount}/{category.permissions.length})
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectAll(category.permissions);
                  }}
                >
                  {allSelected ? "Deselect All" : "Select All"}
                </Button>
              </div>

              {/* Category permissions */}
              {isExpanded && (
                <div className="p-3 space-y-2 bg-white">
                  {category.permissions.map((permission) => {
                    const isSelected = selectedPermissions.includes(permission.id);
                    const isRestricted = permission.id.includes("delete") || 
                                        permission.id.includes("manage_admins") ||
                                        permission.id.includes("manage_integrations");

                    return (
                      <div
                        key={permission.id}
                        className={`flex items-start gap-3 p-2 rounded hover:bg-slate-50 ${
                          isRestricted ? "opacity-75" : ""
                        }`}
                      >
                        <Checkbox
                          id={permission.id}
                          checked={isSelected}
                          onCheckedChange={() => handlePermissionToggle(permission.id)}
                        />
                        <div className="flex-1">
                          <Label
                            htmlFor={permission.id}
                            className="cursor-pointer font-medium text-sm"
                          >
                            {permission.name}
                            {isRestricted && (
                              <span className="ml-2 text-xs text-orange-600 font-normal">
                                (Restricted)
                              </span>
                            )}
                          </Label>
                          <p className="text-xs text-slate-600 mt-0.5">
                            {permission.description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PermissionMatrix;
